import React, {useEffect, useRef, useState} from 'react';
import Card from "../../components/card";
import SelectComponent from "../../components/select-component";
import InputForm from "../../components/input-form";
import TemplateDefault from "../../components/template-default";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {handleFilters} from "../../utils/handle-filters";
import CodeCellRenderer from "../../components/cell-renderer/code-cell-renderer";
import ProjectCellRenderer from "../../components/cell-renderer/project-cell-renderer";
import useSpiderSse from "./hooks/use-spider-sse";
import useContactsFetchData from "./hooks/use-contacts-fetch-data";
import ContactsCellRenderer from "./components/contacts-cell-renderer";
import {useLoaderData} from "react-router-dom";
import StatusCellRenderer from "./components/status-cell-renderer";
import CamerasCellRenderer from "./components/cameras-cell-renderer";
import general from "../../utils/general";
import ContactsModal from "./components/contacts-modal";
import SshModal from "../../components/ssh-modal";
import sshService from "../../services/ssh-service";

const modalityList = [
    {value: 'ACQUISITION', label: 'Aquisição'},
    {value: 'DEMONSTRATION', label: 'Demonstração'},
    {value: 'DEVELOPMENT', label: 'Desenvolvimento'},
    {value: 'LOCATION', label: 'Locação'},
    {value: 'RELATED', label: 'Relacionada'},
]

const initialFilters = {
    project: "",
    ibge: "",
    modality: [
        {value: 'ACQUISITION', label: 'Aquisição'},
        {value: 'DEMONSTRATION', label: 'Demonstração'},
        {value: 'LOCATION', label: 'Locação'},
        {value: 'RELATED', label: 'Relacionada'},
    ],
    resales: []
}

const BASENAME = process.env.PUBLIC_URL;

const Dashboard = () => {
    const [openModal, setOpenModal] = useState(false);
    const [sshModal, setSshModal] = useState(false);
    const [modalData, setModalData] = useState([{
        principal: "",
        data: "",
        title: ""
    }]);

    const [sshModalData, setSshModalData] = useState(null);
    const contactsRender = (data, value) => {
        setModalData({
            principal: value,
            data: data.contacts,
            title: data.name,
        })

        setOpenModal(true)
    }

    const openConnection = (data, id) => {
        setSshModalData(data);
        const ssh = data.ssh;
        if (ssh.length > 1) {
            setSshModal(true);
            return;
        }

        sshService.openSshConnection(id, ssh[0]?.id);
    }

    const project = [
        {field: "name", headerName: "Projeto", cellRenderer: ProjectCellRenderer, flex: 1, minWidth: 120},
        {field: "ibge", headerName: "ID", cellRenderer: CodeCellRenderer,
            cellRendererParams: {
                openConnection: openConnection,
            },flex: 1, minWidth: 110},
        {field: "contact_central", headerName: "Contatos", cellRenderer: ContactsCellRenderer,
            cellRendererParams: {
                contactsRender: contactsRender,
            },
            width: 70},
        {field: "resales", headerName: "Revendas", flex: 1, minWidth: 120},
        {field: "version", headerName: "Versão", flex: 1, minWidth: 120},
        {
            field: "attendances",
            headerName: "Atendimentos em aberto",
            flex: 1, minWidth: 100,
            cellRenderer: ({value, data: {id}}) => {
                const openAttendanceList = () => {
                    window.open(`${BASENAME}/software-attendances?project-id=${id}`, `software-attendances-${id}`);
                }

                return (
                    <div className="text-blue-700 w-full hover:underline" onClick={openAttendanceList}>
                        {value}
                    </div>
                )
            }
        }
    ];

    const spider = [
        {field: "status", headerName: "", cellRenderer: StatusCellRenderer, maxWidth: 40},
        {field: "name", headerName: "Projeto", cellRenderer: ProjectCellRenderer, flex: 1, minWidth: 120},
        {field: "ibge", headerName: "ID", cellRenderer: CodeCellRenderer,
            cellRendererParams: {
                openConnection: openConnection,
            }, flex: 1, minWidth: 110},
        {field: "version", headerName: "Versão", flex: 1, minWidth: 110},
        {field: "occ_group", headerName: "Grupo de ocorrência", flex: 1, minWidth: 105},
        {field: "ipSource", headerName: "IP origem", flex: 1, minWidth: 120},
    ];

    const ocr = [
        {field: "status", headerName: "", cellRenderer: StatusCellRenderer, maxWidth: 40},
        {field: "name", headerName: "Projeto", cellRenderer: ProjectCellRenderer,flex: 1, minWidth: 120},
        {field: "ibge", headerName: "ID", cellRenderer: CodeCellRenderer,
            cellRendererParams: {
                openConnection: openConnection,
            }, flex: 1, minWidth: 120},
        {field: "warning_cameras", flex: 1, minWidth: 160, headerName: "Câmeras com problema", cellRenderer: CamerasCellRenderer},
        {field: "offline_cameras", flex: 1, minWidth: 120, headerName: "Câmeras offline", cellRenderer: CamerasCellRenderer}
    ];

    const columns = {project, spider, ocr};
    const title = {project: "Projetos", spider: "Spider", ocr: "Câmeras de OCR"};

    const { type } = useLoaderData();

    const [filters, setFilters] = useState(initialFilters);
    const [contacts, setContacts] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);

    const {contactsFetch, projects, resales, hash} = useContactsFetchData();
    const {events} = useSpiderSse(hash);

    const gridRef = useRef();

    useEffect(() => {
        const orderColumns = JSON.parse(localStorage.getItem(`${type}-order-columns`));
        const widthColumns = JSON.parse(localStorage.getItem(`${type}-width-columns`));
        const columnsType = columns[type];
        if (orderColumns) general.sortColumns(columnsType, orderColumns);
        if (widthColumns){
            columnsType.map((element, index) => {
                if (element.minWidth) {
                    delete element.flex;
                    element.width = widthColumns[index];
                }
                if (element.width) element.width = widthColumns[index]

                return columnsType
            })
        }
        setColumnDefs(columnsType);
        setFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    useEffect(() => {
        if (contactsFetch) {
            setContacts(handleFilters(contactsFetch, filters))
        }
    }, [filters, contactsFetch]);

    useEffect(() => {
        if (events.length && contactsFetch) {
            for (const value of events) {
                const newContacts = contactsFetch.map(contact => {
                    if (contact.ibge === value.ibge) {
                        contact.ipSource = value.ip;
                        contact.version = value.version;
                        contact.status = value.statusNoc;
                        contact.offline_cameras = value.camerasOffline
                        contact.warning_cameras = value.camerasWarning
                        contact.occ_group = value.occGroup

                    }

                    return contact;
                });

                const filteredContacts = handleFilters(newContacts, filters);

                setContacts(filteredContacts);
            }
        }
    }, [events, filters, contactsFetch]);

    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));

    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem(`${type}-order-columns`, JSON.stringify(columns))
    }

    const columnResizedHandler = (params) => {
        const width = params.api.getAllDisplayedColumns().map(col => col.actualWidth);
        localStorage.setItem(`${type}-width-columns`, JSON.stringify(width))
    };

    return (
        <TemplateDefault title={"Dashboard - " + title[type]}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            isCreatable
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="project"
                            formatCreateLabel={(inputValue) => `${inputValue}`}
                        />
                    </div>

                    <InputForm id="ibge" col="col-span-2" label="ID" type="search" value={filters.ibge} onChange={handleInput}/>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="modality"
                            label="Modalidade"
                            options={modalityList}
                            value={filters.modality}
                            onChange={(value) => handleSelect("modality", value)}
                        />
                    </div>

                    <div className="col-span-3">
                        <SelectComponent
                            isMulti
                            instanceId="resales"
                            label="Revenda"
                            options={resales ?? []}
                            value={filters.resales}
                            onChange={(value) => handleSelect("resales", value)}
                        />
                    </div>

                    <div className={"col-span-12"} style={{minHeight: "calc(100vh - 185px)"}}>
                        <div className={"ag-theme-quartz grid"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={32}
                                rowData={contacts ?? []}
                                columnDefs={columnDefs}
                                animateRows={false}
                                suppressRowClickSelection={true}
                                suppressCellFocus={true}
                                domLayout='autoHeight'
                                gridOptions={{localeText: LocalizationAgGrid}}
                                onColumnMoved={onColumnMoved}
                                onColumnResized={columnResizedHandler}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <ContactsModal open={openModal} set={setOpenModal} modalData={modalData}/>

            <SshModal open={sshModal} set={setSshModal} project={sshModalData}/>

        </TemplateDefault>
    );
};

export default Dashboard;
